import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ar } from '../i18n/ar';
import { en } from '../i18n/en';
import { fr, type Messages, type Plural } from '../i18n/fr';

// Langue de l'interface : français par défaut, anglais, arabe avec sens de lecture RTL (spec 08).
// Distincte de la langue des messages aux assurés (français ou arabe, celle du contrat).

export const PORTAL_LANGUAGES = ['fr', 'en', 'ar'] as const;
export type PortalLanguage = (typeof PORTAL_LANGUAGES)[number];

const DICTIONARIES: Record<PortalLanguage, Messages> = { fr, en, ar };
/** Nom de chaque langue dans sa propre langue (sélecteur). */
export const LANGUAGE_NAMES: Record<PortalLanguage, string> = {
  fr: 'Français',
  en: 'English',
  ar: 'العربية',
};
const STORAGE_KEY = 'ba.language';

export const isPortalLanguage = (value: unknown): value is PortalLanguage =>
  typeof value === 'string' && (PORTAL_LANGUAGES as readonly string[]).includes(value);

interface I18n {
  language: PortalLanguage;
  t: Messages;
  /** Libellé accordé à `n` dans la langue courante. */
  count: (n: number, forms: Plural) => string;
  dir: 'ltr' | 'rtl';
  setLanguage: (language: PortalLanguage) => void;
}

export function pluralize(language: PortalLanguage, n: number, forms: Plural): string {
  const category = new Intl.PluralRules(language).select(n);
  return (forms[category] ?? forms.other).replace('{n}', n.toLocaleString(language));
}

const I18nContext = createContext<I18n | null>(null);

function storedLanguage(): PortalLanguage {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isPortalLanguage(stored) ? stored : 'fr';
  } catch {
    return 'fr';
  }
}

export function I18nProvider({
  children,
  initial,
}: {
  children: ReactNode;
  initial?: PortalLanguage;
}) {
  const [language, setLanguageState] = useState<PortalLanguage>(initial ?? storedLanguage);
  const dir = language === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = dir;
  }, [language, dir]);

  const value = useMemo<I18n>(
    () => ({
      language,
      dir,
      t: DICTIONARIES[language],
      count: (n, forms) => pluralize(language, n, forms),
      setLanguage: (next) => {
        setLanguageState(next);
        try {
          localStorage.setItem(STORAGE_KEY, next);
        } catch {
          // stockage indisponible : préférence non mémorisée
        }
      },
    }),
    [language, dir],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n hors de I18nProvider');
  return value;
}

export const useT = () => useI18n().t;
